import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import axiosInstance from 'utils/axiosUtil';
import { DynamicTable } from 'components';

// A FUNCTION, not a shared object. DynamicTable mutates the query it is handed in place:
// onFilterChange assigns into the same `where` reference (DynamicTable.js), and onOrderChange
// writes into the nested order array. A module-level constant would therefore accumulate the
// user's filter text and sort direction, and resetting to it on reopen would restore the
// polluted value rather than a clean one. Same pattern as FindOrganizationDialog's buildQuery().
const buildDefaultQuery = () => ({
  where: {},
  // id is a tiebreaker: name is not unique (dev has duplicate payor names) and this query is
  // offset-paged, so without it a row can repeat or be skipped at a page boundary.
  order: [['name', 'ASC'], ['id', 'ASC']],
  limit: 10,
  offset: 0,
});

// DynamicTable's column filters fire onQueryChange on every keystroke; collapse them
// into one /Payers request once typing settles (same as FindOrganizationDialog).
const FILTER_DEBOUNCE_MS = 300;

// Floor for the table area so the dialog doesn't jump in height while a page loads.
const TABLE_MIN_HEIGHT = 480;

// Many payor names carry a literal ' (Null)' suffix in the data; display only.
const formatPayorName = (name) => (name || '').replace(/\s*\(Null\)\s*$/i, '');

const columns = [
  {
    label: 'Name',
    key: 'name',
    fulltext: true,
    format: (value) => formatPayorName(value),
  },
  {
    label: 'Type',
    key: 'claims_type',
    style: { width: 220 },
    // The data stores a literal 'Null' string for untyped payors; show it as blank.
    format: (value) => (value === 'Null' ? '' : value),
  },
];

/**
 * Payor picker for the Market Data "Payors" report.
 *
 * Server-side search + paging over /Payers (thousands of rows), filterable by Name
 * (fulltext) and Type (claims_type). Chosen payors are pinned as chips above the table,
 * in selection order -- that order is the chart's legend/colour order.
 *
 * Only payors with a claims_type are claims payors; the rest are client-owned records.
 * That restriction is applied at fetch time under $and rather than in query.where,
 * because DynamicTable owns query.where and clearing the Type filter would delete it.
 */
// maxSelected defaults HERE, not via defaultProps: React 19 ignores defaultProps on function
// components. Left as defaultProps it would be undefined for any caller that omits the prop,
// and `selected.length >= undefined` / `rows.length > undefined` are both false — silently
// turning the cap off and rendering "Select up to undefined payors".
function PayorSelectDialog({ open, initialSelected, maxSelected = 3, onClose, onSave }) {
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState(buildDefaultQuery);
  const [data, setData] = useState({ rows: [], count: 0, loading: true });
  const [fetchQuery, setFetchQuery] = useState(buildDefaultQuery);

  useEffect(() => {
    if (open) {
      setSelected(initialSelected ? [...initialSelected] : []);
      // Fresh objects each time — see buildDefaultQuery.
      setQuery(buildDefaultQuery());
      setFetchQuery(buildDefaultQuery());
    }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setFetchQuery(query), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setData((prev) => ({ ...prev, loading: true }));
    // A Type filter ($like) already excludes NULLs, so the guard is only needed as the
    // default. Not under $and: crud.js files the name $match under Op.and too, and a
    // sibling $and key overwrites it (the name filter silently stopped working).
    const opts = {
      ...fetchQuery,
      where: {
        claims_type: { $ne: null },
        ...fetchQuery.where,
      },
    };
    // offset + limit makes crud return { count, rows } in one request.
    axiosInstance(`/Payers?opts=${encodeURIComponent(JSON.stringify(opts))}`)
      .then((res) => {
        if (!active) return;
        setData({
          rows: res.data?.rows || [],
          count: res.data?.count || 0,
          loading: false,
        });
      })
      .catch((error) => {
        console.error('Error fetching payors:', error);
        if (active) setData({ rows: [], count: 0, loading: false });
      });
    return () => {
      active = false;
    };
  }, [open, fetchQuery]);

  const atMax = selected.length >= maxSelected;

  // DynamicTable MUTATES the array it was given (handleMultiSelect pushes onto it) before
  // calling this, so the array in its props stays mutated even if we reject the change.
  // Returning early here therefore leaves a phantom 4th row in that array: the next deselect
  // filters one entry out of [A,B,C,D], arrives as a length-3 list, passes the check, and the
  // user ends up with the pick they were just told they could not make. Always set state, and
  // truncate rather than bail, so the rejection actually re-renders.
  const onMultiSelect = (rows) => {
    setSelected(rows.slice(0, maxSelected));
  };

  const onRemove = (payor) => {
    setSelected(selected.filter((p) => p.id !== payor.id));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md' scroll='paper'>
      <DialogTitle>Select Payors</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minHeight: 40 }}>
          {selected.map((payor) => (
            <Chip
              key={payor.id}
              label={formatPayorName(payor.name) || `Payor ${payor.id}`}
              variant='outlined'
              onDelete={() => onRemove(payor)}
            />
          ))}
          <Box sx={{ flex: 1 }} />
          <Typography
            variant='body2'
            color={atMax ? 'primary' : 'text.secondary'}
          >
            {atMax
              ? `Maximum of ${maxSelected} selected — remove one to choose another`
              : `Select up to ${maxSelected} payors (${selected.length} selected)`}
          </Typography>
        </Box>
        <Box sx={{ minHeight: `min(${TABLE_MIN_HEIGHT}px, 60vh)`, mt: 1 }}>
          <DynamicTable
            columns={columns}
            idKey='id'
            query={query}
            data={data}
            noDataMessage='No payors found.'
            onQueryChange={setQuery}
            multiSelectedRows={[...selected]}
            onMultiSelect={onMultiSelect}
            ContainerType={React.Fragment}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary'>
          Cancel
        </Button>
        <Button
          onClick={() => onSave(selected)}
          variant='contained'
          color='primary'
          disabled={selected.length === 0}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PayorSelectDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  initialSelected: PropTypes.array,
  maxSelected: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export { PayorSelectDialog, formatPayorName };
